import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import MeetPointsDashboardTable from './MeetPointsDashboardTable';
import HouseTotals from './HouseTotals';
import BonusPoints from './BonusPoints';
import NotReleased from './NotReleased';
import PropTypes from 'prop-types';
import routes from 'routes';
import { clubFeaturesOf, clubForId } from 'utils/clubFeatures';
import { pointsPageControls } from 'utils/meetPoints';
import { teamWordsOf } from 'utils/teamLabel';
import { getUserPermissionLevel } from 'services/auth/tokenService';
import { UserPermissionLevel } from 'constants/auth';
import LoadingContent from 'components/NewDesign/LoadingContent';
import { toast } from 'react-toastify';
import {
  addMeetBonusPoints,
  changeMeetBonusPoints,
  deleteMeetBonusPoints,
  getMeetPointsDashboard,
} from 'api/reportsApi';
import { getMeetVersions } from 'api/meetRegistrationApi';

const POLL_INTERVAL_MS = 60000;

const MeetPointsDashboard = ({ meetId, onDataChange }) => {
  const storageKey = `meetPointsDashboardFilters_${meetId}`;
  // The organisation's word for a house, for the House Totals card and the House column (slice 4a).
  const { clubId } = useParams();
  const club = useSelector((state) => clubForId(state.user, clubId));
  const teamWords = teamWordsOf(club);
  const navigate = useNavigate();
  // A school's members see the page once an Admin releases it (points slice 4b), read only: it checks for
  // new results by itself for Admins only.
  const isAdmin = Number(getUserPermissionLevel()) >= UserPermissionLevel.Admin;
  const { liveUpdates } = pointsPageControls(isAdmin, clubFeaturesOf(club).teams);

  // Load saved filters from SessionStorage
  const getSavedFilters = () => {
    try {
      const saved = sessionStorage.getItem(storageKey);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  };

  const savedFilters = getSavedFilters();

  const [dashboardData, setDashboardData] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [orderBy, setOrderBy] = useState(savedFilters?.orderBy || 'ageGroup');
  const [placeFilter, setPlaceFilter] = useState(savedFilters?.placeFilter || 'top3');
  const [loading, setLoading] = useState(true);
  const [liveStatus, setLiveStatus] = useState(null);

  const includeZeroPoints = placeFilter === 'allAthletes';
  const isInitialLoad = useRef(true);
  // The results version the points shown were worked out from (MeetPointsDashboardDto.resultsVersion).
  const shownVersionRef = useRef(null);

  // Save filters to SessionStorage whenever they change
  useEffect(() => {
    const filters = {
      orderBy,
      placeFilter,
    };
    sessionStorage.setItem(storageKey, JSON.stringify(filters));
  }, [orderBy, placeFilter, storageKey]);

  const fetchDashboardData = useCallback(async () => {
    try {
      if (meetId) {
        if (isInitialLoad.current) {
          setLoading(true);
        }
        const response = await getMeetPointsDashboard(meetId, includeZeroPoints);
        setDashboardData(response.data || null);
        shownVersionRef.current = response.data?.resultsVersion ?? null;
      }
      setLoading(false);
      isInitialLoad.current = false;
    } catch (err) {
      console.error('[MeetPointsDashboard] Fetch dashboard error: ', err);
      setLoading(false);
      isInitialLoad.current = false;
      // A club's member who typed the page's address: a club's page is for Admins only (points slice 4b).
      // The site's own message for a page someone may not open, then back to the meet.
      if (err.response?.status === 403) {
        toast.error('You do not have permission to view the page.');
        navigate(routes.admin.showMeet.url(clubId, meetId));
        return;
      }
      toast.error('Failed to load meet points dashboard');
    }
  }, [meetId, includeZeroPoints, navigate, clubId]);

  // Initial data fetch. The reply says which results version it was worked out from, the baseline the
  // poll below compares with (points slice 4b: before, a second request asked the meet's versions).
  useEffect(() => {
    if (meetId) {
      fetchDashboardData().then(() => {
        console.log('[MeetPointsDashboard] Initial resultsVersion:', shownVersionRef.current);
        if (liveUpdates) setLiveStatus('live');
      });
    }
  }, [fetchDashboardData, meetId, liveUpdates]);

  // Poll the versions endpoint and ask for the points again while what is shown is older than the results.
  // The server keeps the points for a fixed 2 minutes, not worked out again on each upload (points slice
  // 4b: carnival load), so one request per change could get the copy from before the change; asking on
  // every poll until the versions match catches up at the first poll after the copy runs out, within 3
  // minutes of the last change (2 for the copy, 1 between polls). Admins only: a member's page does not
  // check by itself (Doug, 2026-10-25: "Too many users can leave the page up and hammer the server").
  useEffect(() => {
    if (!meetId || !liveUpdates) return;

    const interval = setInterval(async () => {
      // Skip polling until the first reply has said which version it shows
      if (shownVersionRef.current === null) return;

      try {
        const versionResponse = await getMeetVersions(meetId);
        const newVersion = versionResponse.data?.resultsVersion;
        console.log('[MeetPointsDashboard] Poll resultsVersion:', newVersion, '| shown:', shownVersionRef.current);

        if (newVersion !== undefined && newVersion > shownVersionRef.current) {
          console.log('[MeetPointsDashboard] Points older than the results, asking again');
          await fetchDashboardData();
        }
      } catch (err) {
        console.error('[MeetPointsDashboard] Version poll error: ', err);
      }
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [meetId, fetchDashboardData, liveUpdates]);

  // Bonus points (points slice 4c), Admins only: one write, which clears the server's saved copy, then the
  // points asked again so the card and the House Totals show the change. A failure is thrown back to the
  // card, which shows it.
  const saveBonusPoints = useCallback(
    async (lineId, line) => {
      if (lineId) await changeMeetBonusPoints(meetId, lineId, line);
      else await addMeetBonusPoints(meetId, line);
      await fetchDashboardData();
    },
    [meetId, fetchDashboardData]
  );

  const deleteBonusPoints = useCallback(
    async (line) => {
      await deleteMeetBonusPoints(meetId, line.lineId);
      await fetchDashboardData();
    },
    [meetId, fetchDashboardData]
  );

  // The Bonus Points card: for a school with houses; a member sees it only when there are lines.
  const bonusLines = dashboardData?.bonusPoints || [];
  const showsBonusPoints = dashboardData?.houses?.length > 0 && (isAdmin || bonusLines.length > 0);

  // Notify parent component of data changes (for the Export button, the points help beside it, and who
  // sees the page, whose save asks for the points again through refresh)
  useEffect(() => {
    if (onDataChange) {
      onDataChange({
        hasData: dashboardData?.athletes?.length > 0,
        includeZeroPoints,
        explanation: dashboardData?.explanation ?? [],
        teamTotalsVisibility: dashboardData?.teamTotalsVisibility,
        notReleased: dashboardData?.notReleased === true,
        refresh: fetchDashboardData,
      });
    }
  }, [dashboardData, includeZeroPoints, onDataChange, fetchDashboardData]);

  // A school's member before an Admin releases the page: the server sends no points, only that.
  if (dashboardData?.notReleased) {
    return (
      <LoadingContent loading={loading}>
        <NotReleased />
      </LoadingContent>
    );
  }

  return (
    <LoadingContent loading={loading}>
      <HouseTotals
        houses={dashboardData?.houses}
        teamWords={teamWords}
        calculatedAt={dashboardData?.calculatedAt}
        liveStatus={liveStatus}
      />
      {showsBonusPoints && (
        <BonusPoints
          lines={bonusLines}
          houses={dashboardData.houses}
          teamWords={teamWords}
          isAdmin={isAdmin}
          onSave={saveBonusPoints}
          onDelete={deleteBonusPoints}
        />
      )}
      <MeetPointsDashboardTable
        dashboardData={dashboardData}
        searchText={searchText}
        setSearchText={setSearchText}
        orderBy={orderBy}
        setOrderBy={setOrderBy}
        placeFilter={placeFilter}
        setPlaceFilter={setPlaceFilter}
        liveStatus={liveStatus}
        teamWords={teamWords}
      />
    </LoadingContent>
  );
};

MeetPointsDashboard.propTypes = {
  meetId: PropTypes.string,
  onDataChange: PropTypes.func,
};

export default MeetPointsDashboard;
